/**
 * QuitBenefitsPage.jsx
 * Quit Benefits timeline page for QuitPact.
 *
 * User Story 3.2 - Health Awareness
 * AC 3.2.2: Shows time-based milestones of body recovery after quitting.
 *
 * Team: TA37 - The Coders' Assembly
 */

import React, { useState } from 'react';
import './QuitBenefitsPage.css';

const MILESTONES = [
  {
    id: '20min',
    timeLabel: '20 minutes',
    icon: '⚡',
    title: 'Your heart rate drops',
    description:
      'Just 20 minutes after your last cigarette, your heart rate and blood pressure begin to fall back to normal. Your circulation starts to improve almost immediately.',
    color: '#1d9e75',
    badgeColor: '#e1f5ee',
    badgeTextColor: '#0f6e56',
    story: 'The moment you put out that last cigarette, your body wastes no time. Within just 20 minutes, your heart no longer has to work overtime. Nicotine causes your blood vessels to narrow and your heart to beat faster — but as it leaves your system, your cardiovascular system begins to breathe again.',
    fact: 'Nicotine raises your heart rate by up to 20 beats per minute. Within 20 minutes of quitting, it starts returning to its natural rhythm.',
    quote: '"Every moment without a cigarette is a moment your heart gets stronger."',
  },
  {
    id: '12hrs',
    timeLabel: '12 hours',
    icon: '💨',
    title: 'Carbon monoxide clears',
    description:
      'The carbon monoxide from cigarettes drops to normal levels. Your blood can now carry more oxygen to your heart and muscles.',
    color: '#1d9e75',
    badgeColor: '#e1f5ee',
    badgeTextColor: '#0f6e56',
    story: 'Carbon monoxide is a toxic gas found in cigarette smoke. It binds to your red blood cells and stops them from carrying oxygen properly. After 12 hours without smoking, your blood oxygen levels return to normal and your organs start receiving the oxygen they have been starved of.',
    fact: 'Smokers can have up to 10 times more carbon monoxide in their blood than non-smokers. Within 12 hours of quitting, levels drop back to normal.',
    quote: '"Breathing freely is something most of us take for granted — until we get it back."',
  },
  {
    id: '48hrs',
    timeLabel: '48 hours',
    icon: '👃',
    title: 'Taste and smell return',
    description:
      'Your nerve endings start to regrow, and your senses of taste and smell begin coming back. Food and drinks will start to taste noticeably better.',
    color: '#378add',
    badgeColor: '#e6f1fb',
    badgeTextColor: '#185fa5',
    story: 'Smoking damages the tiny nerve endings responsible for taste and smell. Within 48 hours of quitting, these nerve endings begin to repair themselves. Many ex-smokers describe this as one of the most surprising and joyful parts of quitting — suddenly food has flavour again, coffee smells richer, and the world feels more vivid.',
    fact: 'Long-term smokers can lose up to 50% of their taste sensitivity. Most of it returns within a few weeks of quitting.',
    quote: '"The world becomes a richer place when you can smell the rain again."',
  },
  {
    id: '2weeks',
    timeLabel: '2 weeks',
    icon: '🏃',
    title: 'Breathing gets easier',
    description:
      'Your lungs are clearing out and your airways are less inflamed. It is easier to walk, climb stairs, and exercise without getting as puffed.',
    color: '#378add',
    badgeColor: '#e6f1fb',
    badgeTextColor: '#185fa5',
    story: 'After two weeks, the inflammation in your airways begins to reduce. The tiny hair-like structures called cilia — which were paralysed by cigarette smoke — start working again, sweeping out mucus and debris from your lungs. You will notice you are less out of breath walking up stairs or carrying groceries.',
    fact: 'Cilia in the lungs can begin recovering within just a few days of quitting. Within 2 weeks, lung function can improve by up to 30%.',
    quote: '"Taking a deep breath without coughing is a small miracle worth celebrating."',
  },
  {
    id: '3months',
    timeLabel: '3 months',
    icon: '🫁',
    title: 'Lung capacity improves',
    description:
      'Your lung function can improve by up to 10%. The cough and shortness of breath that many smokers live with start to fade.',
    color: '#ba7517',
    badgeColor: '#faeeda',
    badgeTextColor: '#854f0b',
    story: 'Three months in, your lungs are doing serious repair work. The chronic cough that many smokers accept as normal starts to disappear. Your lung capacity increases, meaning your body can take in and use more oxygen with each breath. Physical activity becomes genuinely easier.',
    fact: 'After 3 months of not smoking, lung function can improve by up to 10%, which is significant enough to change how you feel during everyday activities.',
    quote: '"Your lungs have been waiting for this chance to heal. Three months in, they are making the most of it."',
  },
  {
    id: '1year',
    timeLabel: '1 year',
    icon: '❤️',
    title: 'Heart disease risk halved',
    description:
      'After one full year without smoking, your risk of a heart attack is half that of a smoker.',
    color: '#ba7517',
    badgeColor: '#faeeda',
    badgeTextColor: '#854f0b',
    story: 'One year smoke-free is a landmark moment. Your risk of coronary heart disease has dropped by 50% compared to when you were smoking. Your arteries are less stiff, your blood is less likely to clot, and your heart is under far less strain. This is one of the most significant health wins you can achieve.',
    fact: 'Heart disease risk for ex-smokers drops by 50% after just one year. After 15 years, the risk is similar to someone who has never smoked.',
    quote: '"A year without cigarettes is a year your heart got to heal. That is worth more than words."',
  },
  {
    id: '5years',
    timeLabel: '5 years',
    icon: '✨',
    title: 'Stroke risk matches a non-smoker',
    description:
      'Within 5 to 15 years, your risk of stroke drops to the same level as someone who has never smoked.',
    color: '#7f77dd',
    badgeColor: '#eeedfe',
    badgeTextColor: '#534ab7',
    story: 'Five years after quitting, something remarkable happens — your stroke risk falls to the same level as a lifelong non-smoker. Your blood vessels have repaired themselves, blood flow has normalised, and the damage caused by years of smoking has been largely reversed. Your body is extraordinary in its ability to heal.',
    fact: 'Smoking doubles the risk of stroke. But within 5 years of quitting, that elevated risk disappears entirely for most people.',
    quote: '"Five years. Your body did not forget how to heal — it just needed the chance."',
  },
];

const QuitBenefitsPage = () => {
  const [selectedIndex, setSelectedIndex] = useState(null);

  const handleSelect = (index) => {
    setSelectedIndex(index === selectedIndex ? null : index);
  };

  const handleNext = () => {
    if (selectedIndex === null) {
      setSelectedIndex(0);
    } else if (selectedIndex < MILESTONES.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  };

  const handlePrev = () => {
    if (selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
    }
  };

  const selectedMilestone = selectedIndex !== null ? MILESTONES[selectedIndex] : null;

  return (
    <main className="quit-benefits-page" aria-label="Quit benefits timeline">

      {/* -- Page header -- */}
      <header className="quit-benefits-page__header">
        <div className="quit-benefits-page__badge">✨ Quit Benefits</div>
        <h1 className="quit-benefits-page__heading">
          What happens to your body when you quit
        </h1>
        <p className="quit-benefits-page__subheading">
          Tap each milestone to read the full story of your body's recovery.
        </p>
      </header>

      {/* -- Main layout -- */}
      <div className="quit-benefits-page__layout">

        {/* -- Timeline -- */}
        <section className="quit-benefits-page__timeline-container">
          <ol className="quit-timeline" aria-label="Recovery timeline">
            {MILESTONES.map((milestone, index) => {
              const isSelected = selectedIndex === index;

              return (
                <li key={milestone.id} className="quit-timeline__item">

                  {/* -- Dot and line -- */}
                  <div className="quit-timeline__marker">
                    <div
                      className="quit-timeline__dot"
                      style={{
                        backgroundColor: isSelected
                          ? milestone.color
                          : '#e0e0e0',
                        boxShadow: isSelected
                          ? `0 0 0 4px ${milestone.color}22`
                          : 'none',
                      }}
                      aria-hidden="true"
                    />
                    {index < MILESTONES.length - 1 && (
                      <div
                        className="quit-timeline__line"
                        aria-hidden="true"
                      />
                    )}
                  </div>

                  {/* -- Content -- */}
                  <div className="quit-timeline__content">
                    <button
                      className={`quit-timeline__trigger ${isSelected ? 'quit-timeline__trigger--selected' : ''}`}
                      onClick={() => handleSelect(index)}
                      aria-expanded={isSelected}
                      style={
                        isSelected
                          ? {
                              borderColor: milestone.color,
                              backgroundColor: milestone.badgeColor,
                            }
                          : {}
                      }
                    >
                      <div className="quit-timeline__trigger-left">
                        <span
                          className="quit-timeline__time-badge"
                          style={{
                            backgroundColor: milestone.badgeColor,
                            color: milestone.badgeTextColor,
                          }}
                        >
                          {milestone.icon} {milestone.timeLabel}
                        </span>
                        <h3 className="quit-timeline__title">
                          {milestone.title}
                        </h3>
                      </div>
                      <span className="quit-timeline__chevron">
                        {isSelected ? '-' : '+'}
                      </span>
                    </button>

                    {/* -- Short description mobile only -- */}
                    {isSelected && (
                      <div className="quit-timeline__description-wrap quit-timeline__description-wrap--mobile">
                        <p className="quit-timeline__description">
                          {milestone.description}
                        </p>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </section>

        {/* -- Story panel -- */}
        <aside className="quit-benefits-page__story-panel">
          <div className="quit-benefits-page__story-card">

            {selectedMilestone ? (
              <>
                {/* -- Icon -- */}
                <div
                  className="quit-benefits-page__story-icon"
                  style={{ backgroundColor: selectedMilestone.badgeColor }}
                >
                  {selectedMilestone.icon}
                </div>

                {/* -- Title -- */}
                <h2 className="quit-benefits-page__story-title">
                  {selectedMilestone.timeLabel} — {selectedMilestone.title}
                </h2>

                {/* -- Story -- */}
                <p className="quit-benefits-page__story-text">
                  {selectedMilestone.story}
                </p>

                {/* -- Did you know -- */}
                <div className="quit-benefits-page__story-fact">
                  <span className="quit-benefits-page__story-fact-label">
                    Did you know?
                  </span>
                  <p className="quit-benefits-page__story-fact-text">
                    {selectedMilestone.fact}
                  </p>
                </div>

                {/* -- Quote -- */}
                <blockquote className="quit-benefits-page__story-quote">
                  {selectedMilestone.quote}
                </blockquote>

                {/* -- Navigation -- */}
                <div className="quit-benefits-page__story-nav">
                  <button
                    className="quit-benefits-page__nav-btn"
                    onClick={handlePrev}
                    disabled={selectedIndex === 0}
                    type="button"
                  >
                    ← Previous
                  </button>

                  <span className="quit-benefits-page__story-counter">
                    {selectedIndex + 1} of {MILESTONES.length}
                  </span>

                  <button
                    className="quit-benefits-page__nav-btn quit-benefits-page__nav-btn--next"
                    onClick={handleNext}
                    disabled={selectedIndex === MILESTONES.length - 1}
                    type="button"
                  >
                    Next →
                  </button>
                </div>
              </>
            ) : (
              /* -- Default empty state -- */
              <div className="quit-benefits-page__story-empty">
                <div className="quit-benefits-page__story-empty-icon">
                  🌱
                </div>
                <h2 className="quit-benefits-page__story-title">
                  Your recovery starts immediately
                </h2>
                <p className="quit-benefits-page__story-text">
                  The moment you stop smoking, your body begins to repair itself.
                  Every minute, every hour, every day without a cigarette is a
                  step toward a healthier version of you.
                </p>
                <p className="quit-benefits-page__story-text">
                  Tap any milestone on the left to read the full story of what
                  your body goes through at that stage of recovery.
                </p>
                <button
                  className="quit-benefits-page__nav-btn quit-benefits-page__nav-btn--next quit-benefits-page__nav-btn--start"
                  onClick={handleNext}
                  type="button"
                >
                  Start reading →
                </button>
              </div>
            )}

          </div>
        </aside>

      </div>

      {/* -- Footer nudge -- */}
      <section className="quit-benefits-page__cta" aria-label="Next step">
        <p className="quit-benefits-page__cta-text">
          Want to understand your current relapse risk?
        </p>
        <a href="/check-in" className="quit-benefits-page__cta-link">
          Take the check-in
        </a>
      </section>

    </main>
  );
};

export default QuitBenefitsPage;